import { X, Pencil } from 'lucide-react'
import { useRef, useState, useEffect, useCallback } from 'react'
import './Tabs.css'

interface TabsProps {
    openPaths: string[]
    activePath: string | null
    onSelect: (path: string) => void
    onClose: (path: string) => void
    onReorder?: (fromIndex: number, toIndex: number) => void
    onContextMenu?: (path: string, x: number, y: number) => void
    onRename?: (oldPath: string, newPath: string) => void
    externallyModifiedPaths?: string[]
    loadingPaths?: string[]
    canWrite?: boolean
    renamingPath?: string | null
}

export default function Tabs({ openPaths, activePath, onSelect, onClose, onReorder, onContextMenu, onRename, externallyModifiedPaths = [], loadingPaths = [], canWrite = false, renamingPath = null }: TabsProps) {
    // ── All hooks declared unconditionally (React rules-of-hooks) ───

    const barRef = useRef<HTMLDivElement>(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)

    // Drag-to-reorder state
    const [dragIndex, setDragIndex] = useState<number | null>(null)
    const [dropIndex, setDropIndex] = useState<number | null>(null)

    // Inline rename state
    const [editingPath, setEditingPath] = useState<string | null>(null)
    const [draftName, setDraftName] = useState('')
    const editInputRef = useRef<HTMLInputElement>(null)

    // Long-press for mobile context menu
    const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const longPressTriggered = useRef(false)

    const isTouch = typeof window !== 'undefined' && 'ontouchstart' in window

    // Inline rename handlers
    const startRename = useCallback((path: string) => {
        const name = path.split('/').pop() || ''
        setEditingPath(path)
        setDraftName(name)
        // Focus input after render; select filename without extension (Finder/VS Code pattern)
        requestAnimationFrame(() => {
            const input = editInputRef.current
            if (!input) return
            input.focus()
            const dotIdx = name.lastIndexOf('.')
            if (dotIdx > 0) {
                input.setSelectionRange(0, dotIdx)
            } else {
                input.select()
            }
        })
    }, [])

    const commitRename = useCallback(() => {
        if (!editingPath || !onRename) { setEditingPath(null); return }
        const trimmed = draftName.trim()
        const oldName = editingPath.split('/').pop() || ''
        if (trimmed && trimmed !== oldName) {
            const dir = editingPath.substring(0, editingPath.lastIndexOf('/') + 1)
            onRename(editingPath, dir + trimmed)
        }
        setEditingPath(null)
    }, [editingPath, draftName, onRename])

    const cancelRename = useCallback(() => {
        setEditingPath(null)
    }, [])

    // Auto-enter rename mode when renamingPath changes (from context menu)
    useEffect(() => {
        if (renamingPath && openPaths.includes(renamingPath)) {
            startRename(renamingPath)
        }
    }, [renamingPath])

    const updateScrollState = useCallback(() => {
        const el = barRef.current
        if (!el) return
        setCanScrollLeft(el.scrollLeft > 1)
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
    }, [])

    useEffect(() => {
        const el = barRef.current
        if (!el) return

        updateScrollState()
        el.addEventListener('scroll', updateScrollState, { passive: true })

        const ro = new ResizeObserver(updateScrollState)
        ro.observe(el)

        return () => {
            el.removeEventListener('scroll', updateScrollState)
            ro.disconnect()
        }
    }, [updateScrollState, openPaths.length])

    // ── Early return AFTER all hooks (avoids rules-of-hooks violation) ──

    if (openPaths.length === 0) return null

    // ── Non-hook logic and rendering ────────────────────────────────

    const barClasses = [
        'tabs-bar',
        canScrollLeft && 'tabs-bar--can-scroll-left',
        canScrollRight && 'tabs-bar--can-scroll-right',
    ].filter(Boolean).join(' ')

    // Drag handlers (desktop only — touch devices use swipe-scroll)
    const handleDragStart = (e: React.DragEvent, index: number) => {
        if (isTouch) { e.preventDefault(); return }
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', String(index))
        setDragIndex(index)
    }

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        setDropIndex(index)
    }

    const handleDrop = (e: React.DragEvent, toIndex: number) => {
        e.preventDefault()
        const fromIndex = dragIndex
        setDragIndex(null)
        setDropIndex(null)
        if (fromIndex !== null && fromIndex !== toIndex && onReorder) {
            onReorder(fromIndex, toIndex)
        }
    }

    const handleDragEnd = () => {
        setDragIndex(null)
        setDropIndex(null)
    }

    // Context menu handler (right-click)
    const handleContextMenu = (e: React.MouseEvent, path: string) => {
        if (!onContextMenu) return
        e.preventDefault()
        e.stopPropagation()
        onContextMenu(path, e.clientX, e.clientY)
    }

    const handleTouchStart = (path: string, e: React.TouchEvent) => {
        if (!onContextMenu) return
        longPressTriggered.current = false
        const touch = e.touches[0]
        const x = touch.clientX
        const y = touch.clientY
        longPressTimer.current = setTimeout(() => {
            longPressTriggered.current = true
            onContextMenu(path, x, y)
        }, 500)
    }

    const handleTouchEnd = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current)
            longPressTimer.current = null
        }
    }

    const handleTouchMove = () => {
        // Cancel long-press if finger moves
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current)
            longPressTimer.current = null
        }
    }

    return (
        <div ref={barRef} className={barClasses}>
            {openPaths.map((path, index) => {
                const name = path.split('/').pop()
                const isActive = path === activePath
                const isEditing = editingPath === path
                const isLoading = loadingPaths.includes(path)
                const isDragging = dragIndex === index
                const isDropTarget = dropIndex === index && dragIndex !== null && dragIndex !== index

                return (
                    <div
                        key={path}
                        role="tab"
                        tabIndex={0}
                        className={`tab ${isActive ? 'tab-active' : ''} ${isLoading ? 'tab-loading' : ''} ${isDragging ? 'tab-dragging' : ''} ${isDropTarget ? 'tab-drop-target' : ''}`}
                        onClick={() => { if (!isEditing) onSelect(path) }}
                        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !isEditing) { e.preventDefault(); onSelect(path) } }}
                        onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); onClose(path) } }}
                        onContextMenu={(e) => handleContextMenu(e, path)}
                        onTouchStart={(e) => handleTouchStart(path, e)}
                        onTouchEnd={handleTouchEnd}
                        onTouchMove={handleTouchMove}
                        title={path}
                        draggable={!isTouch && !!onReorder && !isEditing}
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                    >
                        {isEditing ? (
                            <input
                                ref={editInputRef}
                                className="tab-edit-input"
                                value={draftName}
                                onChange={e => setDraftName(e.target.value)}
                                onBlur={commitRename}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') { e.preventDefault(); commitRename() }
                                    if (e.key === 'Escape') { e.preventDefault(); cancelRename() }
                                    e.stopPropagation()
                                }}
                                onClick={e => e.stopPropagation()}
                                onMouseDown={e => e.stopPropagation()}
                            />
                        ) : (
                            <>
                                <span
                                    className="tab-name"
                                    onDoubleClick={(e) => {
                                        if (canWrite && onRename) {
                                            e.stopPropagation()
                                            startRename(path)
                                        }
                                    }}
                                >{/\.(db|sqlite|sqlite3|s3db|sdb)$/i.test(name ?? '') ? `📊 ${name}` : name}</span>
                                {isActive && canWrite && onRename && (
                                    <button
                                        className="tab-edit-btn"
                                        onClick={(e) => { e.stopPropagation(); startRename(path) }}
                                        aria-label={`Rename ${name}`}
                                        title="Rename file"
                                    >
                                        <Pencil size={11} />
                                    </button>
                                )}
                            </>
                        )}
                        {externallyModifiedPaths.includes(path) && <span className="tab-external-dot" title="Modified externally" />}
                        {!isEditing && (
                            <button
                                className="tab-close"
                                onClick={(e) => { e.stopPropagation(); onClose(path) }}
                                aria-label={`Close ${name}`}
                            >
                                <X size={12} />
                            </button>
                        )}
                    </div>
                )
            })}
        </div>
    )
}
